import { PAGE_HEADER_NESTED_TABS_WRAPPER_ID } from '@va/constants';
import { TestAttributes } from '@va/types/component';
import { NestedNavigationType, NestedRoute, NestedTab } from '@va/types/navigation';
import { ContextMenuProps, HorizontalScroll, NestedNavigationLink } from '@va/ui/design-system';
import { Portal } from '@va/util/components';
import classNames from 'classnames';
import React, { ElementType, Fragment, Suspense, useCallback, useEffect, useRef } from 'react';
import { Redirect, Route, Switch, useLocation, useRouteMatch } from 'react-router';
import { NestedPageNavigationTab } from './NestedPageNavigationTab';

export type NestedPageNavigationProps = {
  routes: Array<NestedRoute | NestedTab>;
  componentProps?: Record<string, any>;
  onRouteChange?: () => void;
  containerElementId?: string;
  activeColor?: string;
  /** Keeps a tab active while a route nested under it is open, instead of only on an exact match. */
  matchNestedPaths?: boolean;
  tabsWrapperClassName?: string;
  wrapperClassName?: string;
  renderCustomLinkComponent?: (
    props: TestAttributes & {
      url: string;
      onRouteChange?: () => void;
      isActive: boolean;
      className?: string;
      activeColor?: string;
      icon?: ElementType;
      label: string;
      tooltip?: string;
      contextMenu?: ContextMenuProps;
      contextMenuElement?: React.ReactNode;
      endAdornment?: React.ReactNode;
      onHover?: () => void;
      onClick?: () => void;
    },
  ) => React.ReactNode;
};

// TODO Add story file
export const NestedPageNavigation: React.FC<NestedPageNavigationProps> = ({
  routes,
  componentProps,
  onRouteChange,
  containerElementId,
  renderCustomLinkComponent,
  activeColor,
  matchNestedPaths,
  tabsWrapperClassName,
  wrapperClassName,
}) => {
  const { path, url } = useRouteMatch();
  const location = useLocation();

  const isLinkActive = useCallback(
    (linkUrl: string) => (matchNestedPaths ? location.pathname.startsWith(linkUrl) : location.pathname === linkUrl),
    [location.pathname, matchNestedPaths],
  );
  const headerNavigationRef = useRef<HTMLDivElement>(null);

  const scrollToSelectedTab = useCallback(() => {
    const headerNavigationContent = headerNavigationRef?.current;
    if (!headerNavigationContent) return;
    const containerData = headerNavigationContent.children[0];
    const activeElement = containerData.getElementsByClassName('active')[0];
    if (!(activeElement instanceof HTMLElement)) return;
    const activeElementOffsetLeftValue = activeElement.offsetLeft;
    containerData.scrollLeft = activeElementOffsetLeftValue;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerNavigationRef?.current]);

  useEffect(() => {
    scrollToSelectedTab();
    window.addEventListener('resize', scrollToSelectedTab);
    return () => window.removeEventListener('resize', scrollToSelectedTab);
  }, [location.pathname, scrollToSelectedTab]);

  return (
    <Fragment>
      <Portal elementSelector={`#${containerElementId}`}>
        <HorizontalScroll
          ref={headerNavigationRef}
          className={classNames('z-(--zIndex-layout) order-first lg:order-last h-full', wrapperClassName)}
          containerClassName='h-full'
        >
          <div
            id={PAGE_HEADER_NESTED_TABS_WRAPPER_ID}
            className={classNames(
              'min-w-full inline-flex flex-row flex-nowrap justify-start border-b-2 border-gray-gallery bg-white backdrop-filter backdrop-blur-md px-18px h-full',
              tabsWrapperClassName,
            )}
          >
            {routes
              .filter((r) => r.shouldDisplay !== false)
              .map((element) => {
                let label,
                  tooltip,
                  icon,
                  className,
                  content,
                  link,
                  contextMenu,
                  contextMenuElement,
                  rest,
                  onHover,
                  onClick;
                let completeUrl, isTabActive;

                switch (element.type) {
                  case NestedNavigationType.Tab:
                    ({ label, tooltip, icon, className, content, ...rest } = element);

                    return (
                      <NestedPageNavigationTab
                        key={label}
                        label={label}
                        tooltip={tooltip}
                        icon={icon}
                        className={className}
                        isActive={false}
                        hasTooltip={!!tooltip}
                        content={content}
                        data-testid={rest['data-testid']}
                        activeColor={activeColor}
                      />
                    );
                  case NestedNavigationType.Route:
                    ({
                      label,
                      tooltip,
                      icon,
                      className,
                      contextMenu,
                      contextMenuElement,
                      link,
                      onHover,
                      onClick,
                      ...rest
                    } = element);

                    completeUrl = getCompletePath(url, link);
                    isTabActive = isLinkActive(completeUrl);

                    if (renderCustomLinkComponent) {
                      return renderCustomLinkComponent({
                        url: completeUrl,
                        isActive: isTabActive,
                        className,
                        label,
                        icon,
                        tooltip,
                        contextMenu,
                        contextMenuElement,
                        activeColor,
                        onClick,
                        onHover,
                        'data-testid': rest['data-testid'],
                        endAdornment: rest.endAdornment,
                      });
                    }
                    return (
                      <NestedNavigationLink
                        key={completeUrl}
                        onRouteChange={onRouteChange}
                        label={label}
                        isActive={isTabActive}
                        url={completeUrl}
                        icon={icon}
                        className={className}
                        contextMenu={contextMenu}
                        contextMenuElement={contextMenuElement}
                        data-testid={rest['data-testid']}
                        activeColor={activeColor}
                        onHover={onHover}
                        onClick={onClick}
                      />
                    );
                  default:
                    ({
                      label,
                      tooltip,
                      icon,
                      className,
                      contextMenu,
                      contextMenuElement,
                      link,
                      onHover,
                      onClick,
                      ...rest
                    } = element as NestedRoute);
                    completeUrl = getCompletePath(url, link);
                    isTabActive = isLinkActive(completeUrl);
                    return (
                      <NestedNavigationLink
                        key={completeUrl}
                        onRouteChange={onRouteChange}
                        label={label}
                        isActive={isTabActive}
                        url={completeUrl}
                        icon={icon}
                        className={className}
                        contextMenu={contextMenu}
                        contextMenuElement={contextMenuElement}
                        data-testid={rest['data-testid']}
                        activeColor={activeColor}
                        onHover={onHover}
                        onClick={onClick}
                      />
                    );
                }
              })}
            <div className='nav-border border-t-2 border-devil-gray absolute left-0 bottom-0 transition ease-out duration-200 -mb-0.5' />
          </div>
        </HorizontalScroll>
      </Portal>

      <Suspense fallback={null}>
        <Switch>
          {routes
            .filter(
              (r) =>
                r.type === NestedNavigationType.Route &&
                r.shouldDisplay !== false &&
                (r as NestedRoute).shouldRenderRoute !== false,
            )
            .map((r) => {
              const { link, component: Component, exact = false } = r as NestedRoute;
              return (
                <Route exact={exact} key={path + link} path={getCompletePath(path, link)}>
                  <Component {...componentProps} />
                </Route>
              );
            })}
          <Redirect
            exact
            push={false}
            from={path}
            to={{
              pathname: getCompletePath(
                path,
                (routes.find((x) => x.type === NestedNavigationType.Route) as NestedRoute)?.link,
              ),
              state: location.state,
            }}
          />
        </Switch>
      </Suspense>
    </Fragment>
  );
};

function getCompletePath(path: string, routePath: string) {
  if (path === '/') return routePath;
  return path + routePath;
}
